import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { useMemo } from "react";
import type { GuideTourDemoKind } from "./types";

const EASE = [0.42, 0, 0.58, 1] as const;
const OVERLAY_Z = 1300;

export type GuideTourProps = {
  isActive: boolean;
  targetRect: DOMRect | null;
  stepIndex: number;
  totalSteps: number;
  content: string;
  demoKind?: GuideTourDemoKind;
  spotlightPadding?: number;
  /** When true, hide Next (autopilot). Skip always shown. */
  autopilotMode?: boolean;
  /** When true with `autopilotMode`, show Previous so the user can re-read a step; Next stays hidden. */
  autopilotShowPrevious?: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
};

function SpotlightPanels({
  rect,
  pad,
  vw,
  vh,
}: {
  rect: DOMRect;
  pad: number;
  vw: number;
  vh: number;
}) {
  const t = Math.max(0, rect.top - pad);
  const l = Math.max(0, rect.left - pad);
  const r = Math.min(vw, rect.right + pad);
  const b = Math.min(vh, rect.bottom + pad);
  const h = b - t;

  const dim = "bg-black/60";

  return (
    <>
      <motion.div
        className={`pointer-events-auto fixed left-0 right-0 top-0 ${dim}`}
        style={{ height: t, zIndex: OVERLAY_Z }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
      />
      <motion.div
        className={`pointer-events-auto fixed left-0 ${dim}`}
        style={{ top: t, width: l, height: h, zIndex: OVERLAY_Z }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
      />
      <motion.div
        className={`pointer-events-auto fixed ${dim}`}
        style={{ top: t, left: r, right: 0, height: h, zIndex: OVERLAY_Z }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
      />
      <motion.div
        className={`pointer-events-auto fixed left-0 right-0 ${dim}`}
        style={{ top: b, bottom: 0, zIndex: OVERLAY_Z }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
      />
    </>
  );
}

export function GuideTour({
  isActive,
  targetRect,
  stepIndex,
  totalSteps,
  content,
  demoKind = "none",
  spotlightPadding = 8,
  autopilotMode = false,
  autopilotShowPrevious = false,
  onNext,
  onPrev,
  onSkip,
}: GuideTourProps) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 0;
  const vh = typeof window !== "undefined" ? window.innerHeight : 0;

  const rect = useMemo(() => {
    if (!targetRect || !isActive) return null;
    return targetRect;
  }, [isActive, targetRect]);

  const tooltipLayout = useMemo(() => {
    if (!rect) return { top: 24, left: 16, maxW: 320, placement: "bottom" as const };
    const maxW = Math.min(320, vw - 32);
    const margin = 16;
    /** Reserve space for app bottom nav / footer so tour controls are not covered. */
    const footerReserve = 100;
    const estH = 240;
    const belowTop = rect.bottom + spotlightPadding + 12;
    const topCandidate = rect.top - spotlightPadding - 12 - estH;
    const fitsBelow = belowTop + estH <= vh - footerReserve - margin;
    const fitsAbove = topCandidate >= margin;

    let placement: "top" | "bottom";
    let top: number;
    if (fitsBelow) {
      placement = "bottom";
      top = belowTop;
    } else if (fitsAbove) {
      placement = "top";
      top = topCandidate;
    } else {
      placement = "bottom";
      top = Math.max(margin, vh - footerReserve - margin - estH);
    }

    const left = Math.min(
      Math.max(margin, rect.left + rect.width / 2 - maxW / 2),
      vw - maxW - margin
    );
    return { top, left, maxW, placement };
  }, [rect, spotlightPadding, vh, vw]);

  if (typeof document === "undefined") return null;

  return createPortal(
    isActive ? (
      <div className="fixed inset-0" style={{ zIndex: OVERLAY_Z }}>
          {rect && vw > 0 && vh > 0 ? (
            <>
              <SpotlightPanels rect={rect} pad={spotlightPadding} vw={vw} vh={vh} />
              <motion.div
                className="pointer-events-none fixed rounded-xl border-2 border-[color-mix(in_srgb,var(--border-color)_80%,white)] shadow-[0_0_0_4px_color-mix(in_srgb,var(--border-color)_35%,transparent),0_0_28px_color-mix(in_srgb,var(--border-color)_45%,transparent)]"
                style={{
                  zIndex: OVERLAY_Z + 1,
                  top: rect.top - spotlightPadding,
                  left: rect.left - spotlightPadding,
                  width: rect.width + spotlightPadding * 2,
                  height: rect.height + spotlightPadding * 2,
                }}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{
                  opacity: 1,
                  scale: [1, 1.02, 1],
                }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{
                  opacity: { duration: 0.28, ease: EASE },
                  scale: { duration: 1.6, ease: EASE, repeat: Infinity, repeatType: "mirror" },
                }}
              />
            </>
          ) : (
            <motion.div
              className="pointer-events-auto fixed inset-0 bg-black/55"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.28, ease: EASE }}
              style={{ zIndex: OVERLAY_Z }}
            />
          )}

          <motion.div
            className="pointer-events-auto fixed rounded-xl border border-border-color bg-[#0b1220] p-3 text-left shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            style={{
              zIndex: OVERLAY_Z + 2,
              top: tooltipLayout.top,
              left: tooltipLayout.left,
              width: tooltipLayout.maxW,
              paddingBottom: "max(12px, env(safe-area-inset-bottom, 0px))",
            }}
            initial={{ opacity: 0, y: tooltipLayout.placement === "bottom" ? 10 : -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: tooltipLayout.placement === "bottom" ? 8 : -8 }}
            transition={{ duration: 0.32, ease: EASE }}
          >
            <p className="m-0 text-sm leading-snug text-zinc-100">{content}</p>
            {demoKind === "logoPlaceholder" ? (
              <motion.div
                className="mt-2 flex items-center gap-2"
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08, duration: 0.28, ease: EASE }}
              >
                <img
                  src="/pg-logo.svg"
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-lg border border-white/15 bg-black/30 object-contain p-1"
                />
                <span className="text-xs text-zinc-400">Sample logo preview (tour only)</span>
              </motion.div>
            ) : null}

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-zinc-700/60 pt-2">
              <span className="text-xs text-zinc-500">
                {stepIndex + 1} / {totalSteps}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-xs font-medium text-zinc-400 hover:bg-white/10 hover:text-zinc-200"
                  onClick={onSkip}
                >
                  Skip
                </button>
                {!autopilotMode ? (
                  <>
                    <button
                      type="button"
                      className="rounded-md border border-zinc-600 bg-zinc-800/80 px-2.5 py-1 text-xs font-semibold text-zinc-200 hover:bg-zinc-700 disabled:opacity-40"
                      onClick={onPrev}
                      disabled={stepIndex <= 0}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="rounded-md border-2 border-[color-mix(in_srgb,var(--border-color)_50%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_28%,#0c1222)] px-2.5 py-1 text-xs font-semibold text-white hover:opacity-95"
                      onClick={onNext}
                    >
                      Next
                    </button>
                  </>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    {autopilotShowPrevious ? (
                      <button
                        type="button"
                        className="rounded-md border border-zinc-600 bg-zinc-800/80 px-2.5 py-1 text-xs font-semibold text-zinc-200 hover:bg-zinc-700 disabled:opacity-40"
                        onClick={onPrev}
                        disabled={stepIndex <= 0}
                      >
                        Previous
                      </button>
                    ) : null}
                    <span className="text-[11px] text-zinc-500">Playing demo…</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
    ) : null,
    document.body
  );
}
